import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    ProductDetailsDataInterface,
    Image,
    ProductAssociations,
} from "../../common/interfaces/ProductDetailsDataInterface";
import {
    convertProductImagesToLocal,
    loadProductImages,
} from "../../common/utils/imageLoader";
import { findMatchingCategories } from "../../common/utils/findMatchingCategories";
import { CategoryInterface } from "../../common/interfaces/CategoryInterface";
import { ProductInterface } from "../../common/interfaces/ProductInterface";

export const fetchProductDetails = createAsyncThunk(
    "productDetails/fetchProductDetails",
    async (payload: {
        productId: string;
        categories: CategoryInterface[];
        machines?: ProductInterface[];
        accessories?: ProductInterface[];
        products: ProductInterface[];
        getProductById: (
            id: string,
        ) => Promise<ProductDetailsDataInterface | null>;
        getLocalImageUrl: (cdnUrl: string) => Promise<string | null>;
    }) => {
        const {
            productId,
            categories,
            machines,
            accessories,
            products,
            getProductById,
            getLocalImageUrl,
        } = payload;

        try {
            const product = await getProductById(productId);
            const productCategories = findMatchingCategories(
                categories,
                product?.categories || [],
            );

            if (!product) {
                throw new Error("Product not found");
            }

            let localImages: Image[] = [];
            if (product.productImages) {
                localImages = await convertProductImagesToLocal(
                    product.productImages,
                    getLocalImageUrl,
                );
            }

            const productImages = await loadProductImages(
                products,
                getLocalImageUrl,
            );

            const productAssociations = Object.keys(
                product.productAssociations ?? {},
            ).reduce((accumulator: ProductAssociations, associationKey) => {
                const typedKey =
                    associationKey as keyof typeof product.productAssociations;
                const association = product.productAssociations[
                    typedKey
                ] as string[];

                const filterByIdentifier = (
                    items: ProductInterface[] = [],
                ): ProductInterface[] =>
                    items.filter((item) =>
                        association.includes(item.identifier),
                    );

                const matchingProducts = filterByIdentifier(machines || []);
                const matchingAccessories = filterByIdentifier(
                    accessories || [],
                );

                accumulator[associationKey as keyof ProductAssociations] = [
                    ...matchingProducts,
                    ...matchingAccessories,
                ] as [];

                return accumulator;
            }, {} as ProductAssociations);

            return {
                data: product,
                productAssociations: productAssociations,
                productCategories: productCategories,
                localImages,
                productImages,
            };
        } catch (_error) {
            throw new Error("Failed to fetch product details");
        }
    },
);

interface ProductDetailsPageInterface {
    data: ProductDetailsDataInterface | null;
    productAssociations: ProductAssociations | null;
    productCategories: CategoryInterface[];
    localImages: Image[];
    productImages: { [key: string]: string };
    isLoading: boolean;
    isNotFound: boolean;
}

const initialState: ProductDetailsPageInterface = {
    data: null,
    productAssociations: null,
    productCategories: [],
    localImages: [],
    productImages: {},
    isLoading: false,
    isNotFound: false,
};

const productDetailsSlice = createSlice({
    name: "productDetails",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductDetails.pending, (state) => {
                state.isLoading = true;
                state.isNotFound = false;
                state.data = null;
                state.productAssociations = null;
                state.productCategories = [];
                state.localImages = [];
                state.productImages = {};
            })
            .addCase(fetchProductDetails.fulfilled, (state, action) => {
                state.data = action.payload.data;
                state.productAssociations = action.payload.productAssociations;
                state.productCategories = action.payload.productCategories;
                state.localImages = action.payload.localImages;
                state.productImages = action.payload.productImages;
                state.isLoading = false;
                state.isNotFound = false;
            })
            .addCase(fetchProductDetails.rejected, (state) => {
                state.isLoading = false;
                state.isNotFound = true;
            });
    },
});
export const productDetailsReducer = productDetailsSlice.reducer;
